import React, { Fragment } from 'react';
import { shape, string, bool } from 'prop-types';
import classnames from 'classnames';
import AmpImage from 'components/AmpImg';
import Image from 'components/Image';
import BylineImagePlaceholder from 'components/BylineImagePlaceholder';
import TwitterIcon from 'components/Icons/Twitter';
import InstagramIcon from 'components/Icons/Instagram';
import PinterestIcon from 'components/Icons/Pinterest';
import ExternalIcon from 'components/Icons/ExternalLink';
import LazyLoad from 'components/LazyLoad';
import styles from './styles';

const MAX_BYLINE_IMAGE_SIZE = 72;
const SOCIAL_ICON_MAP = {
  twitter: TwitterIcon,
  instagram: InstagramIcon,
  pinterest: PinterestIcon,
  personal: ExternalIcon
};

function PostBylineBottom({
  author: {
    authorUrl,
    imageUrl,
    name,
    jobTitle,
    bio,
    pinterestUrl,
    instagramUrl,
    twitterUrl,
    personalUrl
  },
  variant,
  isAmp,
  isSponsored
}) {
  const socialLinks = Object.entries({
    pinterestUrl,
    twitterUrl,
    instagramUrl,
    personalUrl
  })
    // remove empty social links
    .filter(([_, value]) => Boolean(value)) // eslint-disable-line
    .map(([key, value]) => ({
      service: `${key}`.replace('Url', ''),
      url: value
    }));

  return (
    <div
      className={classnames('PostBylineBottom', {
        [`PostBylineBottom--${variant}`]: true,
        'PostBylineBottom--sponsored': isSponsored,
        'PostBylineBottom--withBio': Boolean(bio || socialLinks.length > 0)
      })}
    >
      <a href={authorUrl}>
        <div className="PostBylineBottom__image">
          {!imageUrl && !isAmp && (
            <BylineImagePlaceholder size={MAX_BYLINE_IMAGE_SIZE / 2} />
          )}
          {imageUrl && (
            <Fragment>
              {!isAmp && (
                <LazyLoad once height={MAX_BYLINE_IMAGE_SIZE}>
                  <Image
                    className="Post__authorImage"
                    url={imageUrl}
                    alt={name}
                    maxSize={{ width: MAX_BYLINE_IMAGE_SIZE, aspectRatio: 1 }}
                    renderOps={{
                      width: MAX_BYLINE_IMAGE_SIZE,
                      height: MAX_BYLINE_IMAGE_SIZE
                    }}
                    sourceSizes={[
                      {
                        layoutWidth: {
                          value: MAX_BYLINE_IMAGE_SIZE,
                          units: `px`
                        }
                      }
                    ]}
                  />
                </LazyLoad>
              )}

              {isAmp && (
                <div
                  className="PostBylineBottom__AMPImageWrap"
                  style={{
                    width: MAX_BYLINE_IMAGE_SIZE,
                    height: MAX_BYLINE_IMAGE_SIZE
                  }}
                >
                  <AmpImage
                    url={imageUrl}
                    alt={name}
                    maxSize={{ width: MAX_BYLINE_IMAGE_SIZE, aspectRatio: 1 }}
                    originalWidth={null}
                    originalHeight={null}
                  />
                </div>
              )}
            </Fragment>
          )}
        </div>
      </a>
      <div className="PostBylineBottom__text">
        <a href={authorUrl}>
          <p className="PostBylineBottom__authorName">{name}</p>
          <p className="PostBylineBottom__authorTitle">
            {jobTitle || 'Contributor'}
          </p>
        </a>
        {bio && (
          <p
            className="PostBylineBottom__authorBio"
            dangerouslySetInnerHTML={{ __html: bio }}
          />
        )}
        {socialLinks.length > 0 && (
          <div>
            <span className="PostBylineBottom_authorFollow">
              <span>Follow {`${name}`.split(' ')[0]}</span>
              <ul className="PostBylineBottom_authorSocialLinks">
                {socialLinks.map(({ service, url }) => {
                  const Icon = SOCIAL_ICON_MAP[service];
                  return (
                    <li
                      key={url}
                      className="PostBylineBottom_authorSocialLinksItem"
                    >
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        <Icon />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </span>
          </div>
        )}
      </div>

      <style jsx>{styles}</style>
    </div>
  );
}

PostBylineBottom.propTypes = {
  variant: string,
  author: shape({
    name: string.isRequired,
    authorUrl: string.isRequired,
    imageUrl: string,
    pinterestUrl: string,
    instagramUrl: string,
    twitterUrl: string,
    personalUrl: string
  }).isRequired,
  isAmp: bool.isRequired,
  isSponsored: bool
};

PostBylineBottom.defaultProps = {
  variant: 'standard',
  isSponsored: false
};

export default PostBylineBottom;
